/**
 * Rubric Creation Form Component
 * Reference implementation following UI Standards
 */

import React, { useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert, CircularProgress,
  Breadcrumbs, Link as MuiLink, Stack, Checkbox, FormControlLabel, IconButton, Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { RubricCreate } from '../../types/rubric';
import { DEFAULT_PREAMBLE, DEFAULT_POSTAMBLE } from '../../constants/defaultPrompts';
import RestoreIcon from '@mui/icons-material/Restore';

const RubricForm: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<RubricCreate>({
    name: '',
    description: '',
    criteria: {},
    is_fundamental: false,
    marking_preamble: DEFAULT_PREAMBLE,  // Initialize with default
    marking_postamble: DEFAULT_POSTAMBLE,  // Initialize with default
    reseller_id: null,
  });

  const [criteriaJson, setCriteriaJson] = useState('{}');
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  // Helper function to clean JSON string of control characters
  const cleanJsonString = (str: string): string => {
    // Remove control characters (ASCII 0-31) except tab, newline, carriage return
    // which are valid in JSON strings when properly escaped
    return str.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '');
  };

  // Reset preamble to default
  const handleResetPreamble = () => {
    setFormData(prev => ({ ...prev, marking_preamble: DEFAULT_PREAMBLE }));
  };

  // Reset postamble to default
  const handleResetPostamble = () => {
    setFormData(prev => ({ ...prev, marking_postamble: DEFAULT_POSTAMBLE }));
  };

  // Lint and format JSON
  const handleLintJson = () => {
    try {
      // First, clean control characters
      const cleaned = cleanJsonString(criteriaJson);

      // Try to parse
      const parsed = JSON.parse(cleaned);

      // Re-stringify with proper formatting
      const formatted = JSON.stringify(parsed, null, 2);

      // Update the field
      setCriteriaJson(formatted);

      // Clear any errors
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.criteria;
        return newErrors;
      });

      setError(null);
      alert('JSON is valid and has been formatted!');
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : 'Invalid JSON';
      setValidationErrors(prev => ({
        ...prev,
        criteria: `JSON Error: ${errorMsg}`
      }));
      alert(`JSON is invalid: ${errorMsg}`);
    }
  };

  // Load a clean template
  const handleLoadTemplate = () => {
    const template = {
      "1.1": {
        "name": "Variable Declaration balX",
        "description": "Correctly declares variable balX and assigns value 25",
        "maxPoints": 2,
        "scoringGuide": {
          "0": "Variable balX not declared or completely incorrect syntax",
          "1": "Variable declared with incorrect syntax or correct syntax but wrong value",
          "2": "Correct declaration with exact syntax and correct value"
        }
      },
      "1.2": {
        "name": "Variable Declaration Y Coordinate",
        "description": "Declares appropriately named variable for y-coordinate with value greater than 300",
        "maxPoints": 2,
        "scoringGuide": {
          "0": "No y-coordinate variable declared or completely incorrect",
          "1": "Variable declared with appropriate name but incorrect value or correct value but poor variable name",
          "2": "Correct declaration with descriptive name and value greater than 300"
        }
      },
      "1.3": {
        "name": "Draw Function Creation",
        "description": "Creates a draw function with correct syntax",
        "maxPoints": 2,
        "scoringGuide": {
          "0": "No draw function created or completely incorrect syntax",
          "1": "Draw function attempted but with syntax errors",
          "2": "Correct draw function declaration"
        }
      },
      "1.4": {
        "name": "Code Integration",
        "description": "Successfully copies provided animation code into program",
        "maxPoints": 1,
        "scoringGuide": {
          "0": "Animation code not copied or mostly missing",
          "1": "All required code elements present"
        }
      },
      "1.5": {
        "name": "Horizontal Ball Movement",
        "description": "Correctly increments balX by exactly 7 to move ball horizontally",
        "maxPoints": 3,
        "scoringGuide": {
          "0": "No increment statement or incorrect variable modified",
          "1": "Correct variable but wrong operator or wrong increment value",
          "2": "Correct variable and operator but incorrect value or correct logic but syntax errors",
          "3": "Correct implementation with exact value of 7 and correct operator"
        }
      },
      "1.6": {
        "name": "Vertical Ball Movement",
        "description": "Moves ball upward at half horizontal speed",
        "maxPoints": 3,
        "scoringGuide": {
          "0": "No vertical movement code or incorrect variable modified",
          "1": "Correct variable but wrong direction or completely wrong value",
          "2": "Correct direction but incorrect value or correct value but syntax errors",
          "3": "Correct implementation with exact value 3.5 moving upward"
        }
      },
      "1.7": {
        "name": "Code Layering and Z-Index",
        "description": "Reorders code so ball appears between uprights",
        "maxPoints": 2,
        "scoringGuide": {
          "0": "No reordering attempted or ball still in wrong position",
          "1": "Partial reordering but not in correct position",
          "2": "Correct layering with ball positioned after left upright but before right upright"
        }
      }
    };

    const formatted = JSON.stringify(template, null, 2);
    setCriteriaJson(formatted);

    // Clear any errors
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.criteria;
      return newErrors;
    });

    alert('JavaScript Animation template loaded! You can now modify or submit it.');
  };

  const validate = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.name || formData.name.trim() === '') {
      errors.name = 'Rubric name is required';
    }

    try {
      // Clean the JSON string before parsing
      const cleanedJson = cleanJsonString(criteriaJson);
      const parsed = JSON.parse(cleanedJson);

      // Check if parsed value is an object (not null, not array)
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        errors.criteria = 'Criteria must be a valid JSON object (not an array or primitive)';
      }

      // Check if it's an empty object
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) && Object.keys(parsed).length === 0) {
        errors.criteria = 'Criteria cannot be an empty object';
      }
    } catch (e) {
      errors.criteria = `Criteria must be valid JSON: ${e instanceof Error ? e.message : 'Invalid JSON'}`;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? (value === '' ? null : Number(value)) : value)
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleCriteriaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCriteriaJson(value);

    // Clear previous errors
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.criteria;
      return newErrors;
    });

    // Real-time validation feedback (optional - only if value is not empty)
    if (value.trim()) {
      try {
        const cleanedValue = cleanJsonString(value);
        const parsed = JSON.parse(cleanedValue);

        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
          setValidationErrors(prev => ({
            ...prev,
            criteria: 'Must be a JSON object (use {} with properties, not an array [])'
          }));
        } else if (Object.keys(parsed).length === 0) {
          setValidationErrors(prev => ({
            ...prev,
            criteria: 'Criteria object cannot be empty'
          }));
        }
      } catch (e) {
        // Don't show parse errors during typing - only on submit
        // This prevents annoying errors while user is still typing
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validate()) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    setLoading(true);

    try {
      // Clean and parse the criteria JSON
      const cleanedJson = cleanJsonString(criteriaJson);

      const payload = {
        ...formData,
        criteria: JSON.parse(cleanedJson),
        description: formData.description || null,
        marking_preamble: formData.marking_preamble || null,
        marking_postamble: formData.marking_postamble || null,
        reseller_id: formData.reseller_id || null,
      };

      await axios.post('/api/rubrics/', payload);
      setSuccess(true);

      setTimeout(() => {
        navigate('/rubrics');
      }, 1500);
    } catch (err: any) {
      console.error('Error creating rubric:', err);

      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          setError(err.response.data.detail);
        } else if (Array.isArray(err.response.data.detail)) {
          const errorMessages = err.response.data.detail
            .map((e: any) => `${e.loc.join('.')}: ${e.msg}`)
            .join(', ');
          setError(errorMessages);
        } else {
          setError('Failed to create rubric. Please check your input.');
        }
      } else {
        setError('Failed to create rubric. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink component="button" variant="body1" onClick={() => navigate('/rubrics')} sx={{ cursor: 'pointer', textDecoration: 'none' }}>
          Rubrics
        </MuiLink>
        <Typography color="text.primary">New Rubric</Typography>
      </Breadcrumbs>

      <Box mb={3}>
        <Typography variant="h4" gutterBottom>Create New Rubric</Typography>
        <Typography variant="body2" color="textSecondary">
          Enter the details for the new grading rubric. Need AI help?{' '}
          <MuiLink component="button" onClick={() => navigate('/rubric-ai-assistant')} sx={{ cursor: 'pointer' }}>
            Try our Rubric AI Assistant
          </MuiLink>
        </Typography>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 3 }}>Rubric created successfully! Redirecting...</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              fullWidth required label="Rubric Name" name="name"
              value={formData.name} onChange={handleChange}
              error={!!validationErrors.name} helperText={validationErrors.name}
              disabled={loading}
            />

            <TextField
              fullWidth label="Description" name="description"
              value={formData.description || ''} onChange={handleChange}
              disabled={loading} multiline rows={2} helperText="Optional"
            />

            <Box>
              <TextField
                fullWidth required label="Criteria (JSON)" name="criteria"
                value={criteriaJson} onChange={handleCriteriaChange}
                error={!!validationErrors.criteria} helperText={validationErrors.criteria || 'Enter rubric criteria as JSON'}
                disabled={loading} multiline rows={10}
              />
              <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleLoadTemplate}
                  disabled={loading}
                >
                  Load JavaScript Template
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleLintJson}
                  disabled={loading}
                >
                  Lint & Format JSON
                </Button>
              </Stack>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              AI Marking Instructions (Pre-populated with System Defaults)
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              These instructions control how the AI marks student submissions. The fields are pre-filled with comprehensive defaults that work well for most cases. Customize them if you need subject-specific instructions or different feedback styles.
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="subtitle2">
                  Prompt Preamble
                </Typography>
                <Tooltip title="Reset to system default">
                  <IconButton size="small" onClick={handleResetPreamble} disabled={loading}>
                    <RestoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <TextField
                fullWidth
                name="marking_preamble"
                value={formData.marking_preamble || ''}
                onChange={handleChange}
                disabled={loading}
                multiline
                rows={6}
                helperText="Sets the AI's role and task. Tells the AI what it is (marking assistant) and what constraints apply (JSON output, use rubric only)."
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="subtitle2">
                  Prompt Postamble
                </Typography>
                <Tooltip title="Reset to system default">
                  <IconButton size="small" onClick={handleResetPostamble} disabled={loading}>
                    <RestoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <TextField
                fullWidth
                name="marking_postamble"
                value={formData.marking_postamble || ''}
                onChange={handleChange}
                disabled={loading}
                multiline
                rows={12}
                helperText="Provides detailed output formatting rules. Specifies the exact structure for feedback (summary + detailed breakdown) and includes an example for consistency."
              />
            </Box>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControlLabel
                control={<Checkbox checked={formData.is_fundamental} onChange={handleChange} name="is_fundamental" disabled={loading} />}
                label="Fundamental Rubric"
              />
              <TextField
                fullWidth type="number" label="Reseller ID" name="reseller_id"
                value={formData.reseller_id || ''} onChange={handleChange}
                disabled={loading} helperText="Optional"
              />
            </Stack>

            <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
              <Button variant="outlined" onClick={() => navigate('/rubrics')} disabled={loading}>Cancel</Button>
              <Button type="submit" variant="contained" color="primary" disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}>
                {loading ? 'Creating...' : 'Create Rubric'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default RubricForm;
